import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import {
  Alert,
  Box,
  FilterChipGroup,
  Link,
  Spinner,
  Text,
  useToast,
} from '@razorpay/blade/components';

import api from 'api';
import {
  CustomFieldFilterApplyResult,
  CustomFieldFilterProps,
  CustomFieldSelectedFilter,
  FilterableCustomFieldDefinition,
} from './types';
import {
  asFilterableDefinition,
  buildCustomFieldClauses,
  createFilterId,
  extractPeopleIdsFromUserIds,
  getFieldSection,
  getFilterableDefinitions,
  getInitialFiltersFromSavedView,
  getInitialFilterValue,
  hasFilterValue,
  isDateDefinition,
  isSelectableDefinition,
  isSupportedFilterDefinition,
} from './utils';
import {
  CUSTOM_FIELD_FILTER_QUERY_KEY,
  FIELDS_BY_USAGE_PAGE_SIZE,
  MAX_FILTERED_EMPLOYEES,
} from './constants';
import { DateFilterChip } from './DateFilterChip';
import { TextFilterChip } from './TextFilterChip';
import { EnumFilterChip } from './EnumFilterChip';
import { AddFilterMenu } from './AddFilterMenu';

export const CustomFieldFilter = ({
  viewType = 'PAYROLL_FILTER',
  disabled = false,
  canManageView = false,
  onApply,
  onClear,
  onError,
  onLoadingChange,
  onSelectedFiltersCountChange,
}: CustomFieldFilterProps) => {
  const [selectedFilters, setSelectedFilters] = useState<CustomFieldSelectedFilter[]>([]);
  const [isAddFilterOpen, setIsAddFilterOpen] = useState(false);
  const [openTextFilterKey, setOpenTextFilterKey] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const hasRestoredSavedView = useRef(false);
  const toast = useToast();
  const updateSavedView = useMutation(api.employeeDataSettings.mutations.updateSavedCustomFieldView);

  // The saved view is an optional enhancement: some roles (for example payroll
  // operators without MANAGE_CUSTOM_FIELDS) get PermissionDenied from
  // GetSavedView. That failure must never block the filter panel — we simply
  // skip restoring/persisting the saved view and fall back to the usage list.
  const savedViewQuery = useQuery(
    [CUSTOM_FIELD_FILTER_QUERY_KEY, 'savedView', viewType],
    () => api.employeeDataSettings.queries.getSavedCustomFieldView({ viewType }),
  );

  const usageFieldsQuery = useQuery(
    [CUSTOM_FIELD_FILTER_QUERY_KEY, 'fieldsByUsage', viewType],
    () =>
      api.employeeDataSettings.queries.listFieldsByUsage({
        usages: ['PAYROLL_FILTER'],
        pageSize: FIELDS_BY_USAGE_PAGE_SIZE,
      }),
    {
      enabled: !savedViewQuery.isLoading,
    },
  );

  const definitions = useMemo(() => {
    const savedDefinitions =
      savedViewQuery.data?.payrollSavedCustomFieldView?.fieldDefinitions
        ?.map(asFilterableDefinition)
        .filter((definition): definition is FilterableCustomFieldDefinition => Boolean(definition)) ??
      [];

    if (savedDefinitions.length > 0) return getFilterableDefinitions(savedDefinitions);

    const usageDefinitions =
      usageFieldsQuery.data?.payrollFieldsByUsage?.definitions
        ?.map(asFilterableDefinition)
        .filter((definition): definition is FilterableCustomFieldDefinition => Boolean(definition)) ??
      [];

    return getFilterableDefinitions(usageDefinitions);
  }, [
    savedViewQuery.data?.payrollSavedCustomFieldView?.fieldDefinitions,
    usageFieldsQuery.data?.payrollFieldsByUsage?.definitions,
  ]);

  const supportedDefinitions = useMemo(
    () => definitions.filter(isSupportedFilterDefinition),
    [definitions],
  );

  const definitionByKey = useMemo(
    () => new Map(supportedDefinitions.map((definition) => [definition.fieldKey, definition])),
    [supportedDefinitions],
  );

  const sectionNames = useMemo(
    () => [...new Set(supportedDefinitions.map(getFieldSection))],
    [supportedDefinitions],
  );

  const queryEmployeeFields = useMutation(api.employeeDataSettings.queries.queryEmployeeFields);

  const persistView = (filters: CustomFieldSelectedFilter[]) => {
    if (!canManageView) return;
    updateSavedView.mutate(
      // BFF now accepts an empty fieldKeys array (removing the last chip persists []).
      { input: { viewType, fieldKeys: filters.map((filter) => filter.fieldKey) } },
      {
        // Persistence must never BLOCK local filtering, but a silent no-op meant
        // removing the last chip appeared to fail (chip reappeared on reload).
        // Surface a retry toast so the user knows the saved layout wasn't updated.
        onError: () => {
          toast.show({
            content: "Couldn't update saved filters. Please retry.",
            color: 'negative',
          });
        },
      },
    );
  };

  // restore once, after the saved view and definitions are both available
  useEffect(() => {
    if (hasRestoredSavedView.current || savedViewQuery.isLoading) return;
    const fieldKeys = savedViewQuery.data?.payrollSavedCustomFieldView?.fieldKeys;
    if (supportedDefinitions.length === 0) return;
    hasRestoredSavedView.current = true;
    const restored = getInitialFiltersFromSavedView(fieldKeys, supportedDefinitions);
    if (restored.length > 0) setSelectedFilters(restored);
  }, [savedViewQuery.isLoading, savedViewQuery.data, supportedDefinitions]);

  const applySelectedFilters = async (nextFilters: CustomFieldSelectedFilter[]) => {
    const filtersWithValues = nextFilters.filter((filter) => hasFilterValue(filter.value));

    if (filtersWithValues.length === 0) {
      setErrorMessage(null);
      onClear?.();
      return;
    }

    try {
      setErrorMessage(null);
      const clauses = buildCustomFieldClauses(filtersWithValues, supportedDefinitions);
      const response = await queryEmployeeFields.mutateAsync({
        clauses,
        pageSize: MAX_FILTERED_EMPLOYEES,
      });

      const result = response.payrollQueryEmployeeFields;
      if (result.nextPageToken) {
        throw new Error('Custom field filter returned more than 10,000 employees. Narrow the filter.');
      }

      const userIds = result.userIds ?? [];
      const applyResult: CustomFieldFilterApplyResult = {
        selectedFilters: filtersWithValues,
        clauses,
        userIds,
        peopleIds: extractPeopleIdsFromUserIds(userIds),
        total: result.total ?? userIds.length,
      };

      onApply(applyResult);
    } catch (error) {
      const normalizedError = error instanceof Error ? error : new Error('Unable to apply filter');
      setErrorMessage(normalizedError.message);
      onError?.(normalizedError);
    }
  };

  const addFilter = (fieldKey: string) => {
    const definition = definitionByKey.get(fieldKey);
    if (!definition) return;

    setSelectedFilters((currentFilters) => {
      if (currentFilters.some((filter) => filter.fieldKey === fieldKey)) {
        return currentFilters;
      }

      const nextFilters = [
        ...currentFilters,
        {
          id: createFilterId(),
          fieldKey,
          value: getInitialFilterValue(definition),
        },
      ];
      persistView(nextFilters);
      return nextFilters;
    });
    setIsAddFilterOpen(false);
  };

  const setFilterValue = (
    fieldKey: string,
    values: CustomFieldSelectedFilter['value'],
    shouldApply = true,
  ) => {
    setSelectedFilters((currentFilters) => {
      const nextFilters = currentFilters.map((filter) =>
        filter.fieldKey === fieldKey ? { ...filter, value: values } : filter,
      );
      if (shouldApply) {
        void applySelectedFilters(nextFilters);
      }
      return nextFilters;
    });
  };

  // Clears only the VALUE; the row stays (values are session state).
  // Also bumps resetVersion so Blade's stateful ENUM/BOOLEAN dropdown and date
  // picker (which keep their own internal selection independent of `value`)
  // remount fresh instead of showing a stale selection when reopened.
  const clearFilterValue = (fieldKey: string, options?: { closeTextDropdown?: boolean }) => {
    const definition = definitionByKey.get(fieldKey);
    if (!definition) return;
    setSelectedFilters((currentFilters) => {
      const nextFilters = currentFilters.map((filter) =>
        filter.fieldKey === fieldKey
          ? {
              ...filter,
              value: getInitialFilterValue(definition),
              resetVersion: (filter.resetVersion ?? 0) + 1,
            }
          : filter,
      );
      void applySelectedFilters(nextFilters);
      return nextFilters;
    });
    if (options?.closeTextDropdown && openTextFilterKey === fieldKey) {
      setOpenTextFilterKey(null);
    }
  };

  // Removes the ROW (the filter list is durable state — persisted).
  const removeFilter = (fieldKey: string) => {
    setSelectedFilters((currentFilters) => {
      const nextFilters = currentFilters.filter((filter) => filter.fieldKey !== fieldKey);
      void applySelectedFilters(nextFilters);
      persistView(nextFilters);
      return nextFilters;
    });
    if (openTextFilterKey === fieldKey) {
      setOpenTextFilterKey(null);
    }
  };

  const clearAllFilters = () => {
    setSelectedFilters((currentFilters) =>
      currentFilters.map((filter) => {
        const definition = definitionByKey.get(filter.fieldKey);
        return definition
          ? {
              ...filter,
              value: getInitialFilterValue(definition),
              resetVersion: (filter.resetVersion ?? 0) + 1,
            }
          : filter;
      }),
    );
    setErrorMessage(null);
    onClear?.();
  };

  const applyOpenTextFilter = () => {
    void applySelectedFilters(selectedFilters);
    setOpenTextFilterKey(null);
  };

  const selectedFiltersWithValues = selectedFilters.filter((filter) => hasFilterValue(filter.value));
  const isLoading = savedViewQuery.isLoading || usageFieldsQuery.isLoading;
  // Error card only when the definitions list itself could not be loaded from
  // ANY source. A failed saved-view lookup alone never reaches this state —
  // definitions fall back to the usage list and the panel renders normally.
  const isDefinitionsError = supportedDefinitions.length === 0 && usageFieldsQuery.isError;
  const isDisabled = disabled || isLoading || queryEmployeeFields.isLoading;

  useEffect(() => {
    onSelectedFiltersCountChange?.(selectedFiltersWithValues.length);
  }, [onSelectedFiltersCountChange, selectedFiltersWithValues.length]);

  useEffect(() => {
    onLoadingChange?.(queryEmployeeFields.isLoading);
  }, [onLoadingChange, queryEmployeeFields.isLoading]);

  useEffect(() => {
    return () => onLoadingChange?.(false);
  }, [onLoadingChange]);

  const renderFilterChip = (filter: CustomFieldSelectedFilter) => {
    const definition = definitionByKey.get(filter.fieldKey);
    if (!definition) return null;

    // resetVersion in the key remounts Blade's stateful pickers on programmatic clears.
    const resetKey = `${filter.id}:${filter.resetVersion ?? 0}`;

    if (isDateDefinition(definition)) {
      return (
        <DateFilterChip
          key={resetKey}
          definition={definition}
          value={filter.value}
          isDisabled={isDisabled}
          onApply={(value) => setFilterValue(definition.fieldKey, value)}
          onClearButtonClick={() => clearFilterValue(definition.fieldKey)}
        />
      );
    }

    if (!isSelectableDefinition(definition)) {
      return (
        <TextFilterChip
          key={filter.id}
          definition={definition}
          value={typeof filter.value === 'string' ? filter.value : ''}
          isOpen={openTextFilterKey === definition.fieldKey}
          isDisabled={isDisabled}
          isClearDisabled={disabled}
          onOpenChange={(isOpen) => setOpenTextFilterKey(isOpen ? definition.fieldKey : null)}
          onChange={(value) => setFilterValue(definition.fieldKey, value, false)}
          onApply={applyOpenTextFilter}
          onClearButtonClick={() =>
            clearFilterValue(definition.fieldKey, { closeTextDropdown: true })
          }
          onClearClick={() => clearFilterValue(definition.fieldKey)}
        />
      );
    }

    return (
      <EnumFilterChip
        key={resetKey}
        definition={definition}
        value={Array.isArray(filter.value) ? filter.value : []}
        isDisabled={isDisabled}
        onChange={(values) => setFilterValue(definition.fieldKey, values)}
        onClearButtonClick={() => clearFilterValue(definition.fieldKey)}
      />
    );
  };

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" gap="spacing.3">
        <Spinner accessibilityLabel="Loading custom field filters" />
        <Text variant="body" size="small">
          Loading custom filters
        </Text>
      </Box>
    );
  }

  // Distinguish a genuine load FAILURE from "org has no filters". Previously an
  // errored definitions query fell through to "No custom payroll filters
  // configured", which is misleading during an HRIS/BFF outage.
  if (isDefinitionsError) {
    return (
      <Box width="100%">
        <Alert
          color="negative"
          title="Couldn't load filters"
          description="Please retry."
          isDismissible={false}
          isFullWidth
          actions={{
            primary: {
              text: 'Retry',
              onClick: () => {
                void savedViewQuery.refetch();
                void usageFieldsQuery.refetch();
              },
            },
          }}
        />
      </Box>
    );
  }

  if (supportedDefinitions.length === 0) {
    return (
      <Text variant="body" size="small" color="surface.text.gray.muted">
        No custom payroll filters configured
      </Text>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap="spacing.3" width="100%">
      <FilterChipGroup showClearButton={false} onClearButtonClick={clearAllFilters}>
        {selectedFilters.map(renderFilterChip)}

        <AddFilterMenu
          isOpen={isAddFilterOpen}
          isDisabled={isDisabled}
          sectionNames={sectionNames}
          definitions={supportedDefinitions}
          selectedFieldKeys={selectedFilters.map((filter) => filter.fieldKey)}
          onOpenChange={setIsAddFilterOpen}
          onAddFilter={addFilter}
          onRemoveFilter={removeFilter}
        />

        {selectedFiltersWithValues.length > 0 && <Link onClick={clearAllFilters}>Clear Filters</Link>}
      </FilterChipGroup>

      {errorMessage && (
        <Text variant="caption" size="medium" color="feedback.text.negative.intense">
          {errorMessage}
        </Text>
      )}
    </Box>
  );
};
